import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { cachedGet, invalidate } from '../lib/apiCache';
import { useLanguage } from '../contexts/LanguageContext';
import { NewsCard, NewsCardSkeleton } from '../components/news/NewsCard';
import { ChevronRight, ArrowUp } from 'lucide-react';
import branding from '../config/branding';
import { useCategories } from '../lib/taxonomy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEW_STORIES_POLL_MS = 60_000;


export default function HomePage() {
  const { t, isHindi } = useLanguage();
  const [topArticles, setTopArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [latestSkip, setLatestSkip] = useState(0);
  const [latestVisibleCount, setLatestVisibleCount] = useState(3);
  const [latestHasMore, setLatestHasMore] = useState(true);
  const [latestLoadingMore, setLatestLoadingMore] = useState(false);
  const [categoryArticles, setCategoryArticles] = useState({});
  const [popularArticles, setPopularArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  // First page of Latest fetched in the background, held until the reader chooses to see it
  const [pendingLatest, setPendingLatest] = useState(null);
  const latestIdsRef = useRef(new Set());
  const latestSectionRef = useRef(null);
  const topStoriesListRef = useRef(null);
  const politicsColumnRef = useRef(null);
  const [politicsColumnMaxHeight, setPoliticsColumnMaxHeight] = useState(null);

  const { label, pathFor } = useCategories();
  // Home page sections: Rajya/Sahar first, then the UP topics
  const categories = ['uttar-pradesh', 'politics', 'social', 'success-story', 'cultural'];

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);

      const TTL = 60_000; // 60s — matches backend TTL

      // Top Stories are ranked on the server by how many outlets cover a story, how trusted they
      // are, breaking feeds, reach and readership; fewer than 6 come back when few stories qualify
      const topRes = await cachedGet(axios, `${API}/public/top-stories`, {}, TTL);
      let rankedTopArticles = topRes.data;
      if (rankedTopArticles[0]) {
        try {
          const leadRes = await axios.get(`${API}/articles/${rankedTopArticles[0].article_id}`, {
            params: { increment_view: 0 },
          });
          rankedTopArticles = rankedTopArticles.map((article, index) => (
            index === 0
              ? { ...article, content: leadRes.data.content, content_hi: leadRes.data.content_hi }
              : article
          ));
        } catch (_) {}
      }
      setTopArticles(rankedTopArticles);

      // Home page lists pass home=1: stories by new or restricted reporters are left out here
      // (they still appear on category, district and author pages; see backend/trust.js)
      const latestRes = await cachedGet(axios, `${API}/public/articles`, { params: { limit: 12, mix: 'categories', home: 1 } }, TTL);
      const latestData = latestRes.data.data || latestRes.data;
      setLatestArticles(latestData);
      setLatestSkip(latestData.length);
      setLatestVisibleCount(3);
      setLatestHasMore(latestData.length === 12);

      // Fetch articles by category
      const categoryPromises = categories.map(cat =>
        cachedGet(axios, `${API}/public/articles`, {
          // Rajya/Sahar shows all UP news (every topic), like its own page
          params: cat === 'uttar-pradesh' ? { region: 'up', limit: 12, home: 1 } : { category: cat, limit: 8, home: 1 },
        }, TTL)
      );
      const categoryResults = await Promise.all(categoryPromises);
      
      const catArticles = {};
      categories.forEach((cat, index) => {
        catArticles[cat] = categoryResults[index].data;
      });
      setCategoryArticles(catArticles);

      // Most read: the recent stories with the most views, for the right rail
      const popularRes = await cachedGet(axios, `${API}/public/most-read`, {}, TTL);
      setPopularArticles(popularRes.data);

    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    latestIdsRef.current = new Set(latestArticles.map((a) => a.article_id));
  }, [latestArticles]);

  // Check for new stories every minute while the tab is visible
  useEffect(() => {
    if (loading) return undefined;
    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await axios.get(`${API}/public/articles`, { params: { limit: 12, mix: 'categories', home: 1 } });
        const fresh = res.data.data || res.data;
        // Imported stories keep the publisher's time, so count unseen ids rather than newer timestamps
        const count = fresh.filter((a) => !latestIdsRef.current.has(a.article_id)).length;
        setPendingLatest(count > 0 ? { articles: fresh, count } : null);
      } catch (_) {}
    };
    const timer = setInterval(check, NEW_STORIES_POLL_MS);
    return () => clearInterval(timer);
  }, [loading]);

  const showNewStories = () => {
    if (!pendingLatest) return;
    const fresh = pendingLatest.articles;
    setLatestArticles(fresh);
    setLatestSkip(fresh.length);
    setLatestVisibleCount(3);
    setLatestHasMore(fresh.length === 12);
    setPendingLatest(null);
    invalidate(`${API}/public/articles`);
    latestSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const loadMoreLatest = async () => {
    try {
      setLatestLoadingMore(true);
      const res = await axios.get(`${API}/public/articles`, { params: { limit: 12, skip: latestSkip, mix: 'categories', home: 1 } });
      const newData = res.data.data || res.data;
      setLatestArticles(prev => [...prev, ...newData]);
      setLatestSkip(prev => prev + newData.length);
      setLatestVisibleCount(prev => prev + 12);
      setLatestHasMore(newData.length === 12);
    } catch (e) {
      console.error(e);
    } finally {
      setLatestLoadingMore(false);
    }
  };

  const font = isHindi ? 'font-hindi' : '';

  // Top Stories: the lead story, the other top stories beside it, and Political on the right
  const [lead, ...rankedTopList] = topArticles;
  const topList = rankedTopList.slice(0, 4);

  useLayoutEffect(() => {
    const list = topStoriesListRef.current;
    if (!list) return undefined;

    const updateColumnHeight = () => {
      if (!window.matchMedia('(min-width: 1024px)').matches) {
        setPoliticsColumnMaxHeight(null);
        return;
      }
      const listBottom = list.getBoundingClientRect().bottom;
      const politicsTop = politicsColumnRef.current?.getBoundingClientRect().top;
      if (politicsTop != null) {
        const availablePoliticsHeight = Math.max(0, listBottom - politicsTop);
        setPoliticsColumnMaxHeight((current) => (
          current != null && Math.abs(current - availablePoliticsHeight) < 1
            ? current
            : availablePoliticsHeight
        ));
      }
    };

    updateColumnHeight();
    const observer = new ResizeObserver(updateColumnHeight);
    observer.observe(list);
    if (politicsColumnRef.current) observer.observe(politicsColumnRef.current);
    window.addEventListener('resize', updateColumnHeight);
    document.fonts?.ready.then(updateColumnHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateColumnHeight);
    };
  }, [loading, lead?.article_id, topList.length]);

  const topIds = new Set(topArticles.map((a) => a.article_id));
  // Latest News under it: newest stories not already in Top Stories
  const stream = latestArticles.filter((a) => !topIds.has(a.article_id));

  // Allow Latest News articles to also appear in their own category column.
  const shown = new Set(topIds);
  const takeUnshown = (list, count) => {
    const picked = (list || []).filter((a) => !shown.has(a.article_id)).slice(0, count);
    picked.forEach((a) => shown.add(a.article_id));
    return picked;
  };
  const [politicsFirst, ...politicsRest] = takeUnshown(categoryArticles.politics, 2);
  const categoryGrid = ['politics', 'social', 'success-story'].map((category) => {
    const latestForCategory = latestArticles.filter((article) => (
      String(article.category || '').toLowerCase() === category
    ));
    const categoryStories = [];
    const ids = new Set();
    [...latestForCategory, ...(categoryArticles[category] || [])].forEach((article) => {
      if (!ids.has(article.article_id) && categoryStories.length < 3) {
        ids.add(article.article_id);
        categoryStories.push(article);
        shown.add(article.article_id);
      }
    });
    return { category, articles: categoryStories };
  });
  // Most Read is an independent views-ranked list and may repeat stories shown elsewhere.
  const mostRead = popularArticles;

  // Section heading: heavy rule, then a serif label one step smaller than "Top Stories"
  // (a link when the section has its own page)
  const headingFont = isHindi ? 'font-hindi-heading' : 'font-heading';
  const SectionHeading = ({ children, to, testId }) => (
    <div className="section-rule mb-4 flex items-center justify-between">
      {to ? (
        <Link to={to} className={`group inline-flex items-center gap-1 text-xl sm:text-[1.375rem] font-bold leading-tight text-[var(--ink)] ${headingFont}`} data-testid={testId}>
          <span className="group-hover:underline underline-offset-4">{children}</span>
          <ChevronRight className="w-5 h-5" />
        </Link>
      ) : (
        <h2 className={`text-xl sm:text-[1.375rem] font-bold leading-tight text-[var(--ink)] ${headingFont}`}>{children}</h2>
      )}
    </div>
  );

  const dashed = 'border-t border-dashed border-[var(--rule)] mt-4 pt-4';

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Helmet>
        <title>{isHindi ? `${branding.nameHi} | ${branding.taglineHi}` : `${branding.name} | ${branding.tagline}`}</title>
        <meta name="description" content={isHindi ? `${branding.nameHi} - ${branding.descriptionHi}` : `${branding.name} - ${branding.description}`} />
        <meta property="og:title" content={`${branding.name} | ${branding.tagline}`} />
        <meta property="og:description" content="Latest breaking news and updates across sports, politics, entertainment, business, and technology." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 pt-5 sm:pt-6">

        {/* ── Top Stories: lead | other top stories | Political ── */}
        <section className="border border-[var(--rule)] p-4 sm:p-6" data-testid="top-stories">
          {/* <div className="flex items-center gap-4 mb-5">
            <h2 className={`text-2xl sm:text-[1.75rem] font-bold text-[var(--ink)] leading-none ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
              {isHindi ? 'मुख्य खबरें' : 'Top Stories'}
            </h2>
            <span className="flex-1 border-t-2 border-[var(--ink)]" />
          </div> */}

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-8">
              <div className="md:col-span-2 lg:col-span-5"><NewsCardSkeleton /></div>
              <div className="lg:col-span-4 space-y-5">
                {[...Array(4)].map((_, i) => <NewsCardSkeleton key={i} variant="compact" />)}
              </div>
              <div className="lg:col-span-3"><NewsCardSkeleton /></div>
            </div>
          ) : lead ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-x-8 gap-y-6">
              {/* The day's lead story */}
              <div className="md:col-span-2 lg:col-span-5">
                <NewsCard article={lead} variant="top" showSummary={true} />
              </div>

              {/* The other top stories */}
              {topList.length > 0 && (
                <div ref={topStoriesListRef} className="border-t border-[var(--rule)] pt-6 md:border-t-0 md:pt-0 lg:col-span-4 lg:border-l lg:pl-8">
                  {topList.map((article, i) => (
                    <div key={article.article_id} className={i > 0 ? 'border-t border-dashed border-[var(--rule)] mt-3 pt-3' : ''}>
                      <NewsCard article={article} variant="list" />
                    </div>
                  ))}
                </div>
              )}

              {/* Political stories on the far right */}
              {politicsFirst && (
                <div className="border-t border-[var(--rule)] pt-6 md:border-t-0 md:pt-0 md:border-l md:pl-8 lg:col-span-3">
                  <Link
                    to={pathFor('politics')}
                    className="group block text-center mb-4"
                    data-testid="view-all-politics"
                  >
                    <span className={`block text-xl font-bold text-[var(--ink)] group-hover:underline underline-offset-4 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                      {isHindi ? label('politics', true) : 'Political'}
                    </span>
                    <span className="flex items-center gap-3 mt-1">
                      <span className="flex-1 border-t border-[var(--rule)]" />
                      <span className={`text-sm text-[var(--ink-muted)] ${isHindi ? 'font-ui font-hindi' : 'font-body italic'}`}>
                        {isHindi ? 'राजनीति की खबरें' : 'Political news'}
                      </span>
                      <span className="flex-1 border-t border-[var(--rule)]" />
                    </span>
                  </Link>
                  <div
                    ref={politicsColumnRef}
                    className="lg:overflow-hidden"
                    style={politicsColumnMaxHeight == null ? undefined : { maxHeight: `${politicsColumnMaxHeight}px` }}
                  >
                    <NewsCard article={politicsFirst} variant="tile" />
                    {politicsRest.map((article) => (
                      <div key={article.article_id} className={dashed}>
                        <NewsCard article={article} variant="headline" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className={`text-center py-12 text-[var(--ink-muted)] ${font}`}>
              {isHindi ? 'अभी कोई मुख्य खबर नहीं है' : 'No top stories right now'}
            </p>
          )}
        </section>

        {/* Three topic columns between the Top Stories hero and Latest News. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
          {categoryGrid.map(({ category, articles }) => (
            <section key={category} className="min-w-0">
              <SectionHeading to={pathFor(category)} testId={`view-all-${category}`}>
                {label(category, isHindi)}
              </SectionHeading>
              {loading ? (
                <div className="space-y-4">
                  {[0, 1, 2].map((item) => <NewsCardSkeleton key={item} variant="compact" />)}
                </div>
              ) : articles.length > 0 ? (
                articles.map((article, index) => (
                  <div key={article.article_id} className={index > 0 ? 'story-rule mt-4 pt-4' : ''}>
                    <NewsCard article={article} variant={index === 0 ? 'compact' : 'headline'} showSummary={false} />
                  </div>
                ))
              ) : (
                <p className={`py-6 text-sm text-[var(--ink-muted)] ${font}`}>No news yet</p>
              )}
            </section>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-x-8 gap-y-10 mt-10">

          {/* ── Latest News: text beside a wide photo, one story per row ── */}
          <section ref={latestSectionRef} className="lg:col-span-8 lg:border-r lg:border-[var(--rule)] lg:pr-8 min-w-0 scroll-mt-16">
            <SectionHeading>{t('latest')}</SectionHeading>

            {pendingLatest && (
              <div className="sticky top-16 md:top-14 z-30 flex justify-center mb-4">
                <button
                  type="button"
                  onClick={showNewStories}
                  className={`flex items-center gap-2 bg-[var(--ink)] hover:bg-black text-white font-ui text-xs font-bold uppercase tracking-wider px-4 py-2.5 shadow-lg ${font}`}
                  data-testid="new-stories-btn"
                >
                  <ArrowUp className="w-4 h-4" />
                  {isHindi
                    ? `${pendingLatest.count} नई खबरें — देखें`
                    : `${pendingLatest.count} new ${pendingLatest.count === 1 ? 'story' : 'stories'} — show`}
                </button>
              </div>
            )}

            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className={`grid md:grid-cols-12 gap-6 animate-pulse ${i > 0 ? 'border-t border-[var(--ink)] mt-6 pt-6' : ''}`}>
                  <div className="md:col-span-5 space-y-3">
                    <div className="h-6 bg-gray-200" />
                    <div className="h-6 bg-gray-200 w-2/3" />
                    <div className="h-4 bg-gray-200" />
                  </div>
                  <div className="md:col-span-7 aspect-[3/2] bg-gray-200" />
                </div>
              ))
            ) : stream.length > 0 ? (
              <>
                {stream.slice(0, latestVisibleCount).map((article, i) => (
                  <div key={article.article_id} className={i > 0 ? 'border-t border-[var(--ink)] mt-6 pt-6' : ''}>
                    <NewsCard article={article} variant="row" />
                  </div>
                ))}
                {(latestHasMore || latestVisibleCount < stream.length) && (
                  <div className="flex justify-center border-t border-[var(--ink)] mt-6 pt-6">
                    <button
                      onClick={() => latestVisibleCount < stream.length
                        ? setLatestVisibleCount(count => count + 12)
                        : loadMoreLatest()}
                      disabled={latestLoadingMore}
                      className={`btn-outline ${font}`}
                    >
                      {latestLoadingMore
                        ? (isHindi ? 'लोड हो रहा है...' : 'Loading...')
                        : (isHindi ? 'और समाचार देखें' : 'Load More')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className={`text-center py-12 text-[var(--ink-muted)] ${font}`}>
                {isHindi ? 'अभी कोई समाचार नहीं है' : 'No news yet'}
              </p>
            )}
          </section>

          {/* ── Right rail: one block per topic, then most read ── */}
          <aside className="lg:col-span-4 min-w-0 space-y-8">
            {loading ? (
              [...Array(4)].map((_, i) => <NewsCardSkeleton key={i} variant="compact" />)
            ) : (
              <>


                {mostRead.length > 0 && (
                  <section>
                    <SectionHeading>{isHindi ? 'सबसे ज़्यादा पढ़ी गई' : 'Most Read'}</SectionHeading>
                    {mostRead.map((article, i) => (
                      <div key={article.article_id} className={i > 0 ? 'story-rule mt-4 pt-4' : ''}>
                        <NewsCard article={article} variant="compact" showSummary={false} />
                      </div>
                    ))}
                  </section>
                )}
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
